import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { inventarioApi } from '@/services/inventarioApi';
import type { InventoryItemRefDto, TipoItem } from '@/types/inventario';

const TIPO_LABELS: Record<TipoItem, string> = {
  PRODUCTO: 'Gabinete',
  PANTALLA_MODULAR: 'Pantalla',
  CONTROLADOR: 'Controlador',
  FUENTE: 'Fuente',
  TARJETA_RECEPTORA: 'Tarjeta',
  OTRO: 'Otro',
};

interface Props {
  value: InventoryItemRefDto | null;
  onChange: (item: InventoryItemRefDto | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Autocompletado de ítems de inventario. Usa /inventario/items/search,
 * que devuelve ítems aunque no tengan stock todavía.
 */
export function InventoryItemPicker({ value, onChange, placeholder = 'Buscar por código o nombre…', disabled }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: items = [], isFetching } = useQuery({
    queryKey: ['inventario-items-search', query],
    queryFn: () => inventarioApi.searchItems(query),
    enabled: open && query.trim().length >= 2,
  });

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{value.nombre}</p>
          <p className="text-xs text-gray-500 font-mono">
            {value.codigo ?? '—'} · {TIPO_LABELS[value.itemType]}
          </p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => { onChange(null); setQuery(''); }}
            className="text-gray-400 hover:text-gray-600 shrink-0"
            aria-label="Cambiar ítem"
          >
            <X size={16} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100"
        />
      </div>
      {open && query.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          {isFetching ? (
            <p className="text-center py-3 text-xs text-gray-500">Buscando…</p>
          ) : items.length === 0 ? (
            <p className="text-center py-3 text-xs text-gray-500">Sin resultados</p>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => { onChange(it); setOpen(false); setQuery(''); }}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0"
              >
                <p className="text-sm font-medium text-gray-900 truncate">{it.nombre}</p>
                <p className="text-xs text-gray-500 font-mono">
                  {it.codigo ?? '—'} · {TIPO_LABELS[it.itemType]}
                </p>
              </button>
            ))
          )}
        </div>
      )}
      {open && query.trim().length > 0 && query.trim().length < 2 && (
        <p className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-500">
          Escribe al menos 2 caracteres
        </p>
      )}
    </div>
  );
}
